import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import TaskModal from './TaskModal';
import '../../css/PublicBoardView.css';
import '../../css/BoardView.css';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

const PRIORITY_BADGE = {
    NONE: null,
    LOW: { label: 'Low', className: 'low' },
    MEDIUM: { label: 'Medium', className: 'medium' },
    HIGH: { label: 'High', className: 'high' },
    URGENT: { label: 'Urgent', className: 'urgent' },
};

const PublicTaskCard = ({ task, boardAbbreviation, onClick }) => {
    const priority = PRIORITY_BADGE[task.priority];
    const isOverdue = task.due_date && new Date(task.due_date) < new Date();

    const boardTaskId = task.board_task_id || (
        boardAbbreviation
            ? `${boardAbbreviation}-${task.board_number || task.id}`
            : `#${task.board_number || task.id}`
    );

    const formatDueDate = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Tomorrow';
        if (diffDays === -1) return 'Yesterday';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div className="TaskCard" onClick={onClick}>
            {task.labels && task.labels.length > 0 && (
                <div className="TaskCard__labels">
                    {task.labels.map((label, i) => (
                        <span key={i} className="TaskCard__label">{label}</span>
                    ))}
                </div>
            )}
            <div className="TaskCard__id-row">
                <span className="TaskCard__board-id">{boardTaskId}</span>
                <span className="TaskCard__id">#{task.id}</span>
            </div>
            <h4 className="TaskCard__title">{task.title}</h4>
            {(task.description || priority || task.due_date) && (
                <div className="TaskCard__meta">
                    {priority && (
                        <span className={`TaskCard__priority TaskCard__priority--${priority.className}`}>
                            {priority.label}
                        </span>
                    )}
                    {task.due_date && (
                        <span className={`TaskCard__due ${isOverdue ? 'TaskCard__due--overdue' : ''}`}>
                            {formatDueDate(task.due_date)}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

const PublicArchiveSection = ({ archive, boardAbbreviation, onTaskClick }) => {
    const [activeTab, setActiveTab] = useState('singles');
    const [expandedGroups, setExpandedGroups] = useState({});

    const toggleGroup = (id) => setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));

    const renderTask = (task) => {
        const boardTaskId = task.board_task_id || (boardAbbreviation ? `${boardAbbreviation}-${task.board_number}` : `#${task.board_number}`);
        const priority = PRIORITY_BADGE[task.priority];
        return (
            <div key={task.id} className="PublicArchive__task" onClick={() => onTaskClick(task)}>
                <span className="PublicArchive__task-id">{boardTaskId}</span>
                <span className="PublicArchive__task-title">{task.title}</span>
                <div className="PublicArchive__task-meta">
                    {task.column_name && <span className="PublicArchive__task-column">{task.column_name}</span>}
                    {priority && <span className={`TaskCard__priority TaskCard__priority--${priority.className}`}>{priority.label}</span>}
                    {task.labels && task.labels.map((l, i) => <span key={i} className="TaskCard__label">{l}</span>)}
                </div>
            </div>
        );
    };

    const renderGroups = (groups) => groups.map(group => (
        <div key={group.id} className="PublicArchive__group">
            <div className="PublicArchive__group-header" onClick={() => toggleGroup(group.id)}>
                <i className={`fas fa-chevron-${expandedGroups[group.id] ? 'down' : 'right'}`} />
                <span className="PublicArchive__group-name">{group.name}</span>
                <span className="PublicArchive__group-count">{group.task_count}</span>
            </div>
            {expandedGroups[group.id] && (
                <div className="PublicArchive__group-tasks">
                    {group.tasks.length === 0
                        ? <p className="PublicArchive__empty">No tasks in this group.</p>
                        : group.tasks.map(renderTask)}
                </div>
            )}
        </div>
    ));

    return (
        <div className="PublicArchive">
            <h3 className="PublicArchive__title">
                <i className="fas fa-archive" />
                Archive Library
            </h3>
            <div className="PublicArchive__tabs">
                {[
                    { key: 'singles', label: `Singles (${archive.singles.length})` },
                    { key: 'label', label: `Label Groups (${archive.label_groups.length})` },
                    { key: 'custom', label: `Custom Groups (${archive.custom_groups.length})` },
                ].map(tab => (
                    <button
                        key={tab.key}
                        className={`PublicArchive__tab${activeTab === tab.key ? ' PublicArchive__tab--active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="PublicArchive__content">
                {activeTab === 'singles' && (
                    archive.singles.length === 0
                        ? <p className="PublicArchive__empty">No individually archived tasks.</p>
                        : archive.singles.map(renderTask)
                )}
                {activeTab === 'label' && (
                    archive.label_groups.length === 0
                        ? <p className="PublicArchive__empty">No label groups.</p>
                        : renderGroups(archive.label_groups)
                )}
                {activeTab === 'custom' && (
                    archive.custom_groups.length === 0
                        ? <p className="PublicArchive__empty">No custom groups.</p>
                        : renderGroups(archive.custom_groups)
                )}
            </div>
        </div>
    );
};

const PublicBoardView = () => {
    const { boardId, taskNumber } = useParams();
    const navigate = useNavigate();

    // Send the visitor to sign in, remembering this public board so they return
    // here afterwards instead of landing on the home page.
    const goToLogin = useCallback(() => {
        navigate('/login', { state: { from: `/public/board/${boardId}` } });
    }, [navigate, boardId]);

    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [showSubmit, setShowSubmit] = useState(false);
    const autoOpenedRef = useRef(null);

    // Auto-open a task when the URL names one (/public/board/:id/task/:number),
    // which is how task-reference chips on the public docs site link in
    // (BBD-129). Mirrors BoardView; here the public archive counts too.
    useEffect(() => {
        if (!board || !taskNumber) return;
        if (autoOpenedRef.current === taskNumber) return;
        const boardNum = parseInt(taskNumber, 10);
        const archive = board.archive;
        const candidates = [
            ...board.columns.flatMap(c => c.tasks),
            ...(archive ? [
                ...archive.singles,
                ...archive.label_groups.flatMap(g => g.tasks),
                ...archive.custom_groups.flatMap(g => g.tasks),
            ] : []),
        ];
        const task = candidates.find(t => t.board_number === boardNum);
        if (task) {
            autoOpenedRef.current = taskNumber;
            setSelectedTask(task);
        }
    }, [board, taskNumber]);

    const closeTask = useCallback(() => {
        setSelectedTask(null);
        if (taskNumber) navigate(`/public/board/${boardId}`, { replace: true });
    }, [taskNumber, boardId, navigate]);

    // Track Firebase auth state for comment support
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
        });
        return unsubscribe;
    }, []);

    // An apiCall-compatible function that attaches Firebase auth if the user is signed in
    const publicApiCall = useCallback(async (path, options = {}) => {
        const headers = { 'Content-Type': 'application/json' };
        const user = auth.currentUser;
        if (user) {
            const token = await user.getIdToken();
            headers['Authorization'] = token;
            headers['Requesting-User-Uid'] = user.uid;
        }
        return fetch(`${API_BASE}${path}`, {
            ...options,
            headers: { ...headers, ...(options.headers || {}) },
        });
    }, []);

    const handleSubmitProposal = async (draft) => {
        try {
            const res = await publicApiCall(`/tasks/boards/${boardId}/submissions/`, {
                method: 'POST',
                body: JSON.stringify(draft),
            });
            if (res.ok) return true;
            const data = await res.json().catch(() => ({}));
            window.alert(data.error || 'Failed to submit task.');
            return false;
        } catch {
            window.alert('Failed to connect to server.');
            return false;
        }
    };

    useEffect(() => {
        const fetchPublicBoard = async () => {
            try {
                const response = await fetch(`${API_BASE}/tasks/boards/${boardId}/public/`);
                if (response.ok) {
                    const data = await response.json();
                    setBoard(data);
                } else if (response.status === 403) {
                    setError('This board is not public.');
                } else if (response.status === 404) {
                    setError('Board not found.');
                } else {
                    setError('Failed to load board.');
                }
            } catch {
                setError('Failed to connect to server.');
            }
            setLoading(false);
        };
        fetchPublicBoard();
    }, [boardId]);

    if (loading) {
        return (
            <div className="PublicBoard">
                <div className="PublicBoard__loading">
                    <div className="LoadingScreen__spinner" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="PublicBoard">
                <div className="PublicBoard__error">
                    <i className="fas fa-lock" />
                    <h2>{error}</h2>
                    <p>This board may be private or may not exist.</p>
                    <button className="PublicBoard__login-btn" onClick={goToLogin}>
                        Sign in to Bluebird Tasks
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="PublicBoard">
            <div className="PublicBoard__navbar">
                <h2 className="PublicBoard__brand" onClick={() => navigate(currentUser ? '/' : '/login')}>
                    Bluebird <span className="PublicBoard__brand-sub">Tasks</span>
                </h2>
                <div className="PublicBoard__navbar-right">
                    <span className="PublicBoard__badge">Public Board</span>
                    {currentUser ? (
                        <button className="PublicBoard__login-link" onClick={() => navigate('/')}>
                            My Boards
                        </button>
                    ) : (
                        <button className="PublicBoard__login-link" onClick={goToLogin}>
                            Sign In
                        </button>
                    )}
                </div>
            </div>

            <div className="PublicBoard__header">
                <div className="PublicBoard__info">
                    <div className="PublicBoard__title-row">
                        {board.abbreviation && (
                            <span className="BoardView__abbr">{board.abbreviation}</span>
                        )}
                        <h1 className="BoardView__title">{board.name}</h1>
                    </div>
                    {board.description && (
                        <p className="BoardView__description">{board.description}</p>
                    )}
                    {board.owner_name && (
                        <span className="PublicBoard__owner">by {board.owner_name}</span>
                    )}
                </div>
                {board.public_allow_task_submissions && (
                    <div className="PublicBoard__actions">
                        {currentUser ? (
                            <button
                                className="PublicBoard__propose-btn"
                                onClick={() => setShowSubmit(true)}
                            >
                                <i className="fas fa-plus" /> Propose a Task
                            </button>
                        ) : (
                            <button
                                className="PublicBoard__propose-btn PublicBoard__propose-btn--signin"
                                onClick={goToLogin}
                                title="Sign in to propose a task"
                            >
                                <i className="fas fa-plus" /> Sign in to propose a task
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="BoardView__columns">
                {board.columns.map(column => (
                    <div key={column.id} className="BoardView__column">
                        <div className="BoardView__column-header">
                            <div className="BoardView__column-title-row">
                                {column.color && (
                                    <span
                                        className="BoardView__column-dot"
                                        style={{ backgroundColor: column.color }}
                                    />
                                )}
                                <h3 className="BoardView__column-name">{column.name}</h3>
                                <span className="BoardView__column-count">
                                    {column.tasks.filter(t => !t.is_archived).length}
                                </span>
                            </div>
                        </div>
                        <div className="BoardView__tasks">
                            {column.tasks
                                .filter(t => !t.is_archived)
                                .map(task => (
                                    <PublicTaskCard
                                        key={task.id}
                                        task={task}
                                        boardAbbreviation={board.abbreviation}
                                        onClick={() => setSelectedTask(task)}
                                    />
                                ))}
                        </div>
                    </div>
                ))}
            </div>

            {board.public_show_archive && board.archive && (
                <PublicArchiveSection
                    archive={board.archive}
                    boardAbbreviation={board.abbreviation}
                    onTaskClick={setSelectedTask}
                />
            )}

            {board.members && board.members.length > 0 && (
                <div className="PublicBoard__members">
                    <h3 className="PublicBoard__members-title">
                        <i className="fas fa-users" />
                        Team Members
                    </h3>
                    <div className="PublicBoard__members-list">
                        {board.members.map((member, i) => (
                            <div key={i} className="PublicBoard__member">
                                <div className="PublicBoard__member-avatar">
                                    {(member.name || member.email || '?')[0].toUpperCase()}
                                </div>
                                <div className="PublicBoard__member-info">
                                    <span className="PublicBoard__member-name">{member.name || 'Unknown'}</span>
                                    {member.email && (
                                        <span className="PublicBoard__member-email">{member.email}</span>
                                    )}
                                </div>
                                <span className="PublicBoard__member-role">{member.role}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {selectedTask && (
                <TaskModal
                    task={selectedTask}
                    columns={board.columns}
                    boardAbbreviation={board.abbreviation}
                    existingLabels={[]}
                    onClose={closeTask}
                    readOnly
                    boardId={boardId}
                    apiCall={publicApiCall}
                    currentUserUid={currentUser?.uid || null}
                    canComment={!!board.public_allow_comments && !!currentUser}
                    showSignInToComment={!!board.public_allow_comments && !currentUser}
                />
            )}

            {showSubmit && (
                <TaskModal
                    mode="submit"
                    task={{ title: '', description: '', priority: 'NONE', labels: [], task_type: 'TASK', column: '' }}
                    columns={board.columns}
                    boardAbbreviation={board.abbreviation}
                    existingLabels={[]}
                    onClose={() => setShowSubmit(false)}
                    onSubmit={handleSubmitProposal}
                />
            )}
        </div>
    );
};

export default PublicBoardView;
